import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMissatgeError } from '@/api/errors';
import {
  useArees,
  useCalendaris,
  useCoberturesCalendaris,
  useCreaCalendari,
  useDepartaments,
  useRotacionsCalendaris,
  type CalendariDto,
} from '@/api/planificacio';
import { Desplegable } from '@/components/Desplegable';
import { Icona, type NomIcona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Camp, Modal } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import {
  avui,
  BADGE_ESTAT_CALENDARI,
  csv,
  descarregaCsv,
  diaClau,
  estatCalendari,
  nomAmbit,
  rotacioActiva,
  type EstatCalendari,
} from './calendari';
import './planificacio.css';

const ICONA_ESTAT: Partial<Record<EstatCalendari, NomIcona>> = {
  ACTIU: 'checkmark-circle',
  BUITS: 'exclamationmark-triangle',
  INSUFICIENT: 'exclamationmark-octagon',
};

function colorCobertura(percentatge: number): string {
  if (percentatge >= 85) return 'var(--green)';
  if (percentatge >= 70) return 'var(--orange)';
  return 'var(--red)';
}

function NouCalendari({
  onTanca,
  duplicar,
  calendaris,
}: {
  onTanca: () => void;
  duplicar: boolean;
  calendaris: CalendariDto[];
}) {
  const { t } = useTranslation('planificacio');
  const missatgeError = useMissatgeError();
  const departaments = useDepartaments();
  const arees = useArees();
  const crea = useCreaCalendari();
  const navega = useNavigate();
  const [origenId, setOrigenId] = useState<number | ''>('');
  const origen = duplicar ? (calendaris.find((c) => c.id === origenId) ?? null) : null;
  const [nom, setNom] = useState('');
  const [ambit, setAmbit] = useState('');

  const triaOrigen = (id: number | '') => {
    setOrigenId(id);
    const o = calendaris.find((c) => c.id === id);
    if (!o) return;
    setNom(t('calendaris.copiaDe', { nom: o.nom }));
    setAmbit(
      o.areaId != null ? `A${o.areaId}` : o.departamentId != null ? `D${o.departamentId}` : '',
    );
  };

  const desa = () => {
    const id = Number(ambit.slice(1));
    crea.mutate(
      {
        nom: nom.trim(),
        departamentId: ambit.startsWith('D') ? id : null,
        areaId: ambit.startsWith('A') ? id : null,
        fusHorari: origen?.fusHorari ?? 'Europe/Madrid',
        recordatoriMinuts: origen?.recordatoriMinuts ?? 1440,
        actiu: true,
      },
      {
        onSuccess: (c) => {
          onTanca();
          navega(`/planificacio/calendaris/${c.id}`);
        },
      },
    );
  };
  return (
    <Modal
      titol={duplicar ? t('calendaris.duplica') : t('calendaris.nou')}
      icona={<Icona nom={duplicar ? 'rectangle-stack' : 'calendar'} className="icon-20 c-blue" />}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--blue btn--s"
            disabled={!nom.trim() || !ambit || (duplicar && !origen) || crea.isPending}
            onClick={desa}
          >
            <Icona nom="plus" />
            {t('calendaris.crea')}
          </button>
        </>
      }
    >
      {duplicar ? (
        <Camp etiqueta={t('calendaris.origen')}>
          <Desplegable<number | ''>
            aria-label={t('calendaris.origen')}
            valor={origenId}
            onCanvia={(v) => triaOrigen(v)}
            opcions={[
              { valor: '', etiqueta: t('calendaris.triaOrigen') },
              ...calendaris.map((c) => ({ valor: c.id, etiqueta: c.nom })),
            ]}
          />
        </Camp>
      ) : null}
      <Camp etiqueta={t('calendaris.nom')}>
        <input className="input" value={nom} onChange={(e) => setNom(e.target.value)} />
      </Camp>
      <Camp etiqueta={t('columnes.ambit')}>
        <Desplegable
          aria-label={t('columnes.ambit')}
          valor={ambit}
          onCanvia={setAmbit}
          placeholder={t('calendaris.triaAmbit')}
          opcions={(departaments.data ?? []).flatMap((d) => [
            {
              valor: `D${d.id}`,
              etiqueta: t('calendaris.totDepartament', { nom: d.nom }),
              grup: d.nom,
            },
            ...(arees.data ?? [])
              .filter((a) => a.departamentId === d.id)
              .map((a) => ({ valor: `A${a.id}`, etiqueta: a.nom, grup: d.nom, nivell: 1 })),
          ])}
        />
      </Camp>
      {crea.isError ? <p className="error-text">{missatgeError(crea.error)}</p> : null}
    </Modal>
  );
}

export function W10Calendaris() {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const navega = useNavigate();
  const calendaris = useCalendaris();
  const departaments = useDepartaments();
  const arees = useArees();
  const [text, setText] = useState('');
  const [modal, setModal] = useState<{ duplicar: boolean } | null>(null);
  const llista = useMemo(() => calendaris.data ?? [], [calendaris.data]);
  const ids = llista.map((c) => c.id);
  const desde = diaClau(avui());
  const cobertures = useCoberturesCalendaris(ids, desde, 30);
  const rotacions = useRotacionsCalendaris(ids);

  const files = llista
    .map((c, i) => {
      const cob = cobertures[i]?.data ?? null;
      const rot = rotacioActiva(rotacions[i]?.data);
      const area = arees.data?.find((a) => a.id === c.areaId);
      const dep = departaments.data?.find(
        (d) => d.id === (area ? area.departamentId : c.departamentId),
      );
      return {
        c,
        cob,
        rot,
        area,
        dep,
        ambit: nomAmbit(c, departaments.data ?? [], arees.data ?? []),
        estat: estatCalendari(c, cob),
      };
    })
    .filter(
      (r) =>
        !text.trim() || `${r.c.nom} ${r.ambit}`.toLowerCase().includes(text.trim().toLowerCase()),
    );

  const exporta = () => {
    descarregaCsv('calendaris.csv', [
      csv([
        t('columnes.calendari'),
        t('columnes.ambit'),
        t('columnes.rotacio'),
        t('columnes.persones'),
        t('columnes.cobertura30'),
        t('columnes.estat'),
      ]),
      ...files.map((r) =>
        csv([
          r.c.nom,
          r.ambit,
          r.rot ? t(`tipusRotacio.${r.rot.periodicitat}`) : t('tipusRotacio.MANUAL'),
          r.rot?.participants.length ?? '',
          r.cob ? `${r.cob.percentatge}%` : '',
          t(`estatCalendari.${r.estat}`),
        ]),
      ),
    ]);
  };

  return (
    <section className="pantalla" data-screen="W-10">
      <Toolbar titol={t('titols.calendaris')}>
        <label className="search pla-cerca">
          <Icona nom="magnifier" />
          <input
            type="search"
            placeholder={t('calendaris.cerca')}
            aria-label={t('calendaris.cerca')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <Permis recurs="cai.calendaris" tipus="ALTA">
          <button
            type="button"
            className="btn btn--s"
            disabled={!llista.length}
            onClick={() => setModal({ duplicar: true })}
          >
            <Icona nom="rectangle-stack" />
            {t('calendaris.duplica')}
          </button>
        </Permis>
        <button type="button" className="btn btn--s" onClick={exporta} disabled={!files.length}>
          <Icona nom="doc-export" />
          {t('comu.exporta')}
        </button>
        <Permis recurs="cai.calendaris" tipus="ALTA">
          <button
            type="button"
            className="btn btn--blue btn--s"
            onClick={() => setModal({ duplicar: false })}
          >
            <Icona nom="plus" />
            {t('calendaris.nou')}
          </button>
        </Permis>
      </Toolbar>
      <div className="content">
        <div className="panel">
          {calendaris.isError ? (
            <ErrorCarrega onReintenta={() => void calendaris.refetch()} />
          ) : files.length === 0 ? (
            <div className="empty">
              <Icona nom="calendar" />
              {calendaris.isLoading ? t('comu.carregant') : t('calendaris.buit')}
            </div>
          ) : (
            <table className="taula taula--clicable">
              <thead>
                <tr>
                  <th>{t('columnes.calendari')}</th>
                  <th>{t('columnes.ambit')}</th>
                  <th>{t('columnes.rotacio')}</th>
                  <th className="num">{t('columnes.persones')}</th>
                  <th style={{ width: 180 }}>{t('columnes.cobertura30')}</th>
                  <th>{t('columnes.estat')}</th>
                  <th>{t('columnes.recordatori')}</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {files.map((r) => (
                  <tr
                    key={r.c.id}
                    data-calendari={r.c.id}
                    onClick={() => navega(`/planificacio/calendaris/${r.c.id}`)}
                  >
                    <td>
                      <div className="pla-titol">{r.c.nom}</div>
                      <div className="pla-sub">
                        {r.area
                          ? t('calendaris.area', { nom: r.area.nom })
                          : t('calendaris.departament', { nom: r.dep?.nom ?? r.ambit })}
                      </div>
                    </td>
                    <td>{r.area && r.dep ? `${r.dep.nom} / ${r.area.nom}` : r.ambit}</td>
                    <td>
                      {r.rot ? t(`tipusRotacio.${r.rot.periodicitat}`) : t('tipusRotacio.MANUAL')}
                    </td>
                    <td className="num">{r.rot?.participants.length ?? '—'}</td>
                    <td>
                      {r.cob ? (
                        <div
                          className="progress"
                          role="progressbar"
                          aria-valuenow={r.cob.percentatge}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          title={f.percentatge(r.cob.percentatge)}
                        >
                          <i
                            style={{
                              width: `${r.cob.percentatge}%`,
                              background: colorCobertura(r.cob.percentatge),
                            }}
                          />
                        </div>
                      ) : (
                        <span className="dim">—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge badge--${BADGE_ESTAT_CALENDARI[r.estat]}`}
                        data-estat={r.estat}
                      >
                        {ICONA_ESTAT[r.estat] ? (
                          <Icona nom={ICONA_ESTAT[r.estat] as NomIcona} />
                        ) : null}
                        {t(`estatCalendari.${r.estat}`)}
                      </span>
                    </td>
                    <td className="t-caption-2 dim">
                      {t('calendaris.recordatoriHores', {
                        count: Math.round((r.c.recordatoriMinuts ?? 1440) / 60),
                      })}
                    </td>
                    <td>
                      <Icona nom="chevron-right" className="pla-chev" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {modal ? (
        <NouCalendari
          duplicar={modal.duplicar}
          calendaris={llista}
          onTanca={() => setModal(null)}
        />
      ) : null}
    </section>
  );
}
